import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../utils/cn';
import { pressProps } from '../../utils/motion';
type Variant = 'primary' | 'action' | 'outline' | 'subtle' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';
const variants: Record<Variant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary/90',
  action: 'bg-action text-on-action hover:bg-action/90',
  outline: 'border border-hairline bg-surface text-ink hover:border-primary/50',
  subtle: 'bg-oat text-ink hover:bg-oat/70',
  ghost: 'text-ink-2 hover:text-ink hover:bg-oat',
  danger: 'bg-danger text-white hover:bg-danger/90'
};
const sizes: Record<Size, string> = {
  sm: 'h-9 px-3.5 text-[13px] gap-1.5 rounded-md',
  md: 'h-11 px-5 text-sm gap-2 rounded-lg',
  lg: 'h-12 px-7 text-[15px] gap-2.5 rounded-xl'
};
export function buttonClasses(variant: Variant = 'primary', size: Size = 'md', className?: string): string {
  return cn('inline-flex items-center justify-center font-medium tracking-[-0.01em] transition-colors duration-150 ease-out disabled:opacity-45 disabled:pointer-events-none select-none', variants[variant], sizes[size], className);
}
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  full?: boolean;
}
export function Button({
  variant = 'primary',
  size = 'md',
  full,
  className,
  children,
  ...rest
}: ButtonProps) {
  const reduce = useReducedMotion();
  return <motion.button {...pressProps(reduce)} className={buttonClasses(variant, size, cn(full && 'w-full', className))} {...rest} data-id="element-934" data-mp="xo8m5cuse6g6">
      {children}
    </motion.button>;
}
const MotionLink = motion(Link);
interface ButtonLinkProps {
  to: string;
  variant?: Variant;
  size?: Size;
  full?: boolean;
  className?: string;
  children: React.ReactNode;
  state?: unknown;
}
export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  full,
  className,
  children,
  state
}: ButtonLinkProps) {
  const reduce = useReducedMotion();
  return <MotionLink to={to} state={state} {...pressProps(reduce)} className={buttonClasses(variant, size, cn(full && 'w-full', className))} data-id="element-935" data-mp="1tfu4r2e2grsa">
      {children}
    </MotionLink>;
}
interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
}
export function IconButton({
  label,
  className,
  children,
  ...rest
}: IconButtonProps) {
  const reduce = useReducedMotion();
  return <motion.button {...pressProps(reduce)} aria-label={label} title={label} className={cn('inline-flex h-10 w-10 items-center justify-center rounded-lg border border-hairline bg-surface text-ink-2', 'transition-colors duration-150 ease-out hover:text-ink hover:border-primary/40', className)} {...rest} data-id="element-936" data-mp="2c7d2out8hfng">
      {children}
    </motion.button>;
}